// Date-only values (expiry dates and recorded P&L dates) are calendar labels;
// only timestamps should be converted between display time zones.
export function calendarDateInTimeZone(value: Date | number, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const part = (type: string) =>
    parts.find((item) => item.type === type)!.value;
  const year = Number(part("year"));
  const month = Number(part("month"));
  const day = Number(part("day"));
  return {
    year,
    monthIndex: month - 1,
    day,
    dateStr: `${part("year")}-${part("month")}-${part("day")}`,
  };
}

// Used for calendar boundaries and noon-based journal entries. Resolve each
// boundary independently so DST days can correctly span 23 or 25 hours.
export function calendarTimeToUTC(
  date: string,
  timeZone: string,
  hour = 0,
  minute = 0,
): Date {
  const [year, month, day] = date.split("-").map(Number);
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  let instant = wall;
  const seen = new Set<number>();
  for (let attempt = 0; attempt < 4; attempt++) {
    const parts = formatter.formatToParts(instant);
    const part = (type: string) =>
      Number(parts.find((item) => item.type === type)!.value);
    const rendered = Date.UTC(
      part("year"),
      part("month") - 1,
      part("day"),
      part("hour"),
      part("minute"),
      part("second"),
    );
    const adjustment = wall - rendered;
    if (!adjustment) return new Date(instant);
    seen.add(instant);
    const next = instant + adjustment;
    // A skipped spring-forward wall time is advanced across the gap.
    if (seen.has(next)) return new Date(Math.max(instant, next));
    instant = next;
  }
  return new Date(instant);
}

export function nextCalendarDate(date: string) {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1))
    .toISOString()
    .slice(0, 10);
}

export function timeZoneLabel(
  timeZone: string,
  at: Date | number,
  locale = "en-US",
) {
  return (
    new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value ?? timeZone
  );
}
